import {
  DecimalPipe,
  HashLocationStrategy,
  LocationStrategy
} from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { EffectsModule } from '@ngrx/effects';
import {
  RouterStateSerializer,
  StoreRouterConnectingModule
} from '@ngrx/router-store';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CookieModule } from 'ngx-cookie';

import { AccountModule } from './account/account.module';

import { AccountBlockGuard } from './account/guard/account-block-guard';

import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';

import { AuthenticationService } from './authentication/authentication.service';
import { ComponentsShowcaseModule } from './components-showcase/components-showcase.module';
import { DeviceManagementService } from './device-management/device-management.service';
import { IphoneModule } from './iphone/iphone.module';
import { LoginModule } from './login/login.module';
import { PageNotFoundModule } from './page-not-found/page-not-found.module';
import { ResetPasswordModule } from './reset-password/reset-password.module';
import { CompanySettingsServiceHelper } from './settings/services/company-settings-helper.service';
import { CompanySettingsService } from './settings/services/company-settings.service';
import {
  effects as CompanySettingsEffect,
  reducers as CompanySettingsReducers
} from './settings/store';
import { BrowserDetectionService } from './shared/browser/browser-detection.service';
import { WindowService } from './shared/browser/window.service';

import { ComponentsModule } from './shared/components/components.module';
import { FlashService } from './shared/components/flash/flash.service';

import { PageLoaderService } from './shared/components/loader/page-loader.service';
import { ModalModule } from './shared/components/modal/modal.module';
import { PillModule } from './shared/components/pill/pill.module';
import { CsvExporterService } from './shared/csv-exporter.service';
import { DynamicTranslationModule } from './shared/dynamic-translation/dynamic-translation.module';
import { DynamicTranslationService } from './shared/dynamic-translation/dynamic-translation.service';
import { ErrorLogService } from './shared/error/error-log.service';

import { LOGGING_ERROR_HANDLER_PROVIDERS } from './shared/error/logging-error-handler';
import { ChainResolver } from './shared/establishment/chain-resolver';
import { EstablishmentService } from './shared/establishment/establishment.service';
import { FormModule } from './shared/form/form.module';
import { AuthenticationGuard } from './shared/guards/authentication-guard';
import { PermissionGuard } from './shared/guards/permission-guard';
import { VersionGuard } from './shared/guards/version-guard';
import { RpcService } from './shared/http/rpc.service';
import { RestHttpInterceptor } from './shared/http/rest-interceptor';
import { ImageModule } from './shared/image/image.module';
import { ModulesService } from './shared/modules/modules.service';
import { NavigationComponent } from './shared/navigation/navigation.component';
import { NavigationModule } from './shared/navigation/navigation.module';
import { PermissionService } from './shared/permission/permission.service';
import { RestaurantConnectionService } from './shared/restaurantConnection/restaurant-connection.service';
import { ServerService } from './shared/server/server.service';
import { SessionService } from './shared/session/session.service';
import { SideBarService } from './shared/sidebar/sidebar.service';
import { VersionService } from './shared/version/version.service';
import {
  clearState,
  CustomSerializer,
  effects as routeEffects,
  reducers as routeReducers
} from './store';
import { SwitchAccountModule } from './switch-account/switch-account.module';
import { UnsupportedBrowserModule } from './unsupported-browser/unsupported-browser.module';
import { UpdateWarningModule } from './update-warning/update-warning.module';
import { UsersService } from './user-management/users/users.service';
import { WaiterMobileModule } from './waiter/waiter-mobile/waiter-mobile.module';
import { TokenService } from './authentication/token.service';
import { TasksModule } from './tasks/tasks.module';
import { DownloadService } from './shared/download-service';
import { LsAccountingModule } from './ls-accounting/ls-accounting.module';
import { NewAccountModule } from './new-account/account.module';
import { MerchantAccountNotificationComponent } from './shared/components/merchant-account-notification/merchant-account-notification.component';
import { FdmPrecheckWizardModule } from './shared/fdm-precheck-wizard/fdm-precheck-wizard.module';

export const metaReducers: Array<MetaReducer<any>> = [clearState];

@NgModule({
  declarations: [
    AppComponent,
    NavigationComponent,
    MerchantAccountNotificationComponent
  ],
  imports: [
    NewAccountModule,
    AccountModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    ComponentsModule,
    ComponentsShowcaseModule,
    DynamicTranslationModule,
    FormModule,
    FormsModule,
    FdmPrecheckWizardModule,
    HttpClientModule,
    ImageModule,
    IphoneModule,
    LoginModule,
    ModalModule,
    NavigationModule.forRoot(),
    PageNotFoundModule,
    PillModule,
    ResetPasswordModule,
    SwitchAccountModule,
    UnsupportedBrowserModule,
    UpdateWarningModule,
    WaiterMobileModule,
    CookieModule.forRoot(),
    // These need to be in this order or the store won't work (until comment: Stop).
    StoreModule.forRoot(routeReducers, { metaReducers }),
    EffectsModule.forRoot(routeEffects),

    StoreModule.forFeature('company', CompanySettingsReducers),
    EffectsModule.forFeature(CompanySettingsEffect),

    StoreRouterConnectingModule,
    StoreDevtoolsModule.instrument({
      logOnly: true
    }),
    TasksModule,
    LsAccountingModule
    // Stop
  ],
  providers: [
    AccountBlockGuard,
    AuthenticationGuard,
    AuthenticationService,
    TokenService,
    BrowserDetectionService,
    ChainResolver,
    CompanySettingsService,
    CompanySettingsServiceHelper,
    CsvExporterService,
    DecimalPipe,
    DeviceManagementService,
    DynamicTranslationService,
    ErrorLogService,
    EstablishmentService,
    FlashService,
    LOGGING_ERROR_HANDLER_PROVIDERS,
    ModulesService,
    PermissionGuard,
    PermissionService,
    RestaurantConnectionService,
    RpcService,
    ServerService,
    SessionService,
    SideBarService,
    UsersService,
    VersionGuard,
    VersionService,
    WindowService,
    PageLoaderService,
    DownloadService,
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RestHttpInterceptor,
      multi: true
    },
    {
      provide: RouterStateSerializer,
      useClass: CustomSerializer
    }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {}
